/**
 * Canonical production Home host that recipients are sent to when opening a shared chat.
 *
 * A share link is consumed by a recipient who has no relationship to the
 * tenant/origin where the chat was created (e.g. a chat shared from
 * `hello.atlassian.net` should still open on Home, not on `hello`). Production
 * tenants use this as the default for generated links.
 */
export const DEFAULT_SHARE_BASE_URL = 'https://home.atlassian.com';
export const STAGING_SHARE_BASE_URL = 'https://home.stg.atlassian.com';

/**
 * Path segment placed between the base URL and the opaque share token.
 */
const SHARE_PATH = '/chat/share';
const SHARE_PREVIEW_PATH = `${SHARE_PATH}/preview`;

export type BuildShareConversationPreviewUrlArgs = {
	conversationId: string;
	messageId: string;
	baseUrl?: string;
	orgId?: string;
};

const getCurrentOrigin = (): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return window.location.origin;
};

export const getDefaultShareBaseUrl = (origin: string | undefined = getCurrentOrigin()): string => {
	if (origin?.includes('jira-dev.com') || origin?.includes('home.stg.atlassian.com')) {
		return STAGING_SHARE_BASE_URL;
	}

	return DEFAULT_SHARE_BASE_URL;
};

/**
 * Builds the user-facing shareable URL for a chat snapshot from the
 * opaque `shareToken` returned by `PATCH /api/v2/share`.
 *
 * By default the link points at a Home host so that recipients are always
 * routed to the shared chat viewer regardless of which tenant/origin the
 * sender generated the link from. The current browser origin is only used to
 * choose between production Home and staging Home. When an `orgId` is supplied,
 * the link is scoped through the Atlassian Home organization path so Home can
 * resolve the share from the sender's organization context.
 *
 * An explicit `baseUrl` can be supplied to override the host — primarily so
 * staging/dev testers can open links against a specific non-production Home
 * host.
 *
 * @param shareToken - Opaque token identifying the share record.
 * @param baseUrl - Optional origin to build the link against (e.g. a staging
 *   Home host). Defaults to production Home, or staging Home when the current
 *   origin is a staging tenant or staging Home.
 * @param orgId - Optional organization id used to scope the Home route.
 * @returns The full shareable URL to place on the clipboard / link field.
 */
export const buildShareConversationUrl = (
	shareToken: string,
	baseUrl: string = getDefaultShareBaseUrl(),
	orgId?: string,
): string => {
	// Normalise away any trailing slash on the base URL so we never emit a
	// double slash before the share path.
	const normalisedBase = baseUrl.replace(/\/+$/, '');
	const orgPath = orgId ? `/o/${encodeURIComponent(orgId)}` : '';
	return `${normalisedBase}${orgPath}${SHARE_PATH}/${shareToken}`;
};

/**
 * Builds the sender preview URL for a chat snapshot without requiring a
 * share record to exist first. Home uses the conversation and message ids to
 * load the preview page through the preview API.
 */
export const buildShareConversationPreviewUrl = ({
	conversationId,
	messageId,
	baseUrl = getDefaultShareBaseUrl(),
	orgId,
}: BuildShareConversationPreviewUrlArgs): string => {
	const normalisedBase = baseUrl.replace(/\/+$/, '');
	const orgPath = orgId ? `/o/${encodeURIComponent(orgId)}` : '';
	const params = new URLSearchParams({ conversationId, messageId });
	return `${normalisedBase}${orgPath}${SHARE_PREVIEW_PATH}?${params.toString()}`;
};
